<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingRules',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getContainer = (...args) => {
    return {
      self: $fn.value.urlFor('create_object_by_type'),
      object: 'list',
      data: [],
    };
  };
  const getPayload = (...args) => {
    const [editor] = args;
    const exclude = [];

    return editor.isNew
      ? editor.getData({ exclude })
      : editor.getChanges({ exclude, skipEmbeddedLists: true });
  };

  const formId = ref(null);
  const formName = ref('billingListItem');
  const items = ref(null);
  const selectedItem = ref(null);
  const createItem = ref(false);
  const editingItemID = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_1040167445267876_style = computed(() => ({
    minHeight: '300px',
  }));
  const b_8527427120829392_style = computed(() => ({
    minWidth: '20px',
  }));
  const b_8363486085232621_style = computed(() => ({
    minWidth: '20px',
  }));
  const b_4186377094414664_style = computed(() => ({
    minHeight: '300px',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    items: items.value,
    selectedItem: selectedItem.value,
    createItem: createItem.value,
    editingItemID: editingItemID.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_1040167445267876_style: b_1040167445267876_style.value,
    b_8527427120829392_style: b_8527427120829392_style.value,
    b_8363486085232621_style: b_8363486085232621_style.value,
    b_4186377094414664_style: b_4186377094414664_style.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_items = async () => {
    return $fn.value.fetch('list_object_by_type', {
      parameters: { object_type: 'billing-rule' },
      asResource: true,
    });
  };
  const $$load_selectedItem = async () => {
    return items.value.length ? items.value[0] : null;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'items' },
      { name: 'selectedItem', dependsOn: ['items'] },
      { name: 'createItem' },
      { name: 'editingItemID' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_1040167445267876_style', nodeId: '1040167445267876' },
      { name: 'b_8527427120829392_style', nodeId: '8527427120829392' },
      { name: 'b_8363486085232621_style', nodeId: '8363486085232621' },
      { name: 'b_4186377094414664_style', nodeId: '4186377094414664' },
    ],
    loaders: {
      formId: $$load_formId,
      items: $$load_items,
      selectedItem: $$load_selectedItem,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      items,
      selectedItem,
      createItem,
      editingItemID,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_1040167445267876_style,
      b_8527427120829392_style,
      b_8363486085232621_style,
      b_4186377094414664_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('BillingRules|Title', 'Billing Rules'),
          '(' + (items.value ? items.value.length : 0) + ')',
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_2eg1ss3vm_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2095089031469157_showModal_dgud7yitp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('import_object_by_type', {
          object_type: 'billing-rule',
        }),
        accept: '.json',
      },
    };
  };
  const act_5105091150304644_script_nzi4nmzb3_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_5105091150304644_script_nzi4nmzb3_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5105091150304644_script_nzi4nmzb3_value_callable($event);
      },
    };
  };
  const act_5105091150304644_script_mf8csgv80_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_5105091150304644_script_mf8csgv80_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5105091150304644_script_mf8csgv80_value_callable($event);
      },
    };
  };
  const act_8387648034966287_script_tbb2ysy4l_value_callable = async (
    $event
  ) => {
    return (editingItemID.value = $event.scope.item.id);
  };
  const act_8387648034966287_script_tbb2ysy4l_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8387648034966287_script_tbb2ysy4l_value_callable($event);
      },
    };
  };
  const act_6964713724626057_rest_56g5l699h_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      resource: $event.scope.item,
    };
  };
  const act_2272469684735879_script_3op2xo2de_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_2272469684735879_script_3op2xo2de_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2272469684735879_script_3op2xo2de_value_callable($event);
      },
    };
  };
  const act_2272469684735879_script_dxoazoqo3_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_2272469684735879_script_dxoazoqo3_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2272469684735879_script_dxoazoqo3_value_callable($event);
      },
    };
  };
  const act_3373384577795494_script_fjko0iwsx_value_callable = async (
    $event
  ) => {
    return (createItem.value = true);
  };
  const act_3373384577795494_script_fjko0iwsx_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3373384577795494_script_fjko0iwsx_value_callable($event);
      },
    };
  };
  const act_3601807637515735_script_6s3ah2psz_value_callable = async (
    $event
  ) => {
    return (createItem.value = true);
  };
  const act_3601807637515735_script_6s3ah2psz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3601807637515735_script_6s3ah2psz_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_2eg1ss3vm =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_2eg1ss3vm_getActionArgs,
    });
  const inst_evh_7315092382398562_close_m88zsh2nq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_2eg1ss3vm,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_m88zsh2nq],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_26xv2wq7q =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0ilw5cvid =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_26xv2wq7q,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0ilw5cvid],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_de0nx5myy =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_w6a0vc59f =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_de0nx5myy,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_w6a0vc59f],
    event: 'close',
  });
  const act_4615685607825208_reloadComponentData_sebzc8uko =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_9483ddt01 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_sebzc8uko,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_9483ddt01],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_reloadComponentData_our206p9c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2095089031469157_close_gboev69i9 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_our206p9c,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_2095089031469157_showModal_dgud7yitp =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_dgud7yitp_getActionArgs,
      events: [inst_evh_2095089031469157_close_gboev69i9],
    });
  const inst_evh_2095089031469157_clickPreventStop_3g9k5mkfv =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_dgud7yitp,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_3g9k5mkfv],
      event: 'click.prevent.stop',
    });
  const act_5105091150304644_script_nzi4nmzb3 = new actionTypes.ScriptAction({
    actionArgs: act_5105091150304644_script_nzi4nmzb3_getActionArgs,
  });
  const inst_evh_5105091150304644_saved_55y6ze8mu =
    new actionTypes.EventHandlerImpl({
      action: act_5105091150304644_script_nzi4nmzb3,
      event: 'saved',
      displayName: 'script',
    });
  const act_5105091150304644_reloadComponentData_69w40r0bb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5105091150304644_saved_nc74kpuwj =
    new actionTypes.EventHandlerImpl({
      action: act_5105091150304644_reloadComponentData_69w40r0bb,
      event: 'saved',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5105091150304644_saved = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_5105091150304644_saved_55y6ze8mu,
      inst_evh_5105091150304644_saved_nc74kpuwj,
    ],
    event: 'saved',
  });
  const act_5105091150304644_script_mf8csgv80 = new actionTypes.ScriptAction({
    actionArgs: act_5105091150304644_script_mf8csgv80_getActionArgs,
  });
  const inst_evh_5105091150304644_cancelEdit_m2ey1an1n =
    new actionTypes.EventHandlerImpl({
      action: act_5105091150304644_script_mf8csgv80,
      event: 'cancelEdit',
      displayName: 'script',
    });
  const inst_evh_5105091150304644_cancelEdit =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5105091150304644_cancelEdit_m2ey1an1n],
      event: 'cancelEdit',
    });
  const act_8387648034966287_script_tbb2ysy4l = new actionTypes.ScriptAction({
    actionArgs: act_8387648034966287_script_tbb2ysy4l_getActionArgs,
    displayName: 'editItem',
  });
  const inst_evh_8387648034966287_clickPreventStop_pxwl28y4e =
    new actionTypes.EventHandlerImpl({
      action: act_8387648034966287_script_tbb2ysy4l,
      event: 'click.prevent.stop',
      displayName: 'editItem',
    });
  const inst_evh_8387648034966287_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8387648034966287_clickPreventStop_pxwl28y4e],
      event: 'click.prevent.stop',
    });
  const act_6964713724626057_reloadComponentData_r4qybkw5b =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6964713724626057_deleted_0f1635r4u =
    new actionTypes.EventHandlerImpl({
      action: act_6964713724626057_reloadComponentData_r4qybkw5b,
      event: 'deleted',
      displayName: 'reloadComponentData',
    });
  const act_6964713724626057_rest_56g5l699h = new actionTypes.RestAction({
    actionArgs: act_6964713724626057_rest_56g5l699h_getActionArgs,
    displayName: 'deleteItem',
    events: [inst_evh_6964713724626057_deleted_0f1635r4u],
  });
  const inst_evh_6964713724626057_clickPreventStop_pcqiqtt0e =
    new actionTypes.EventHandlerImpl({
      action: act_6964713724626057_rest_56g5l699h,
      event: 'click.prevent.stop',
      displayName: 'deleteItem',
    });
  const inst_evh_6964713724626057_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6964713724626057_clickPreventStop_pcqiqtt0e],
      event: 'click.prevent.stop',
    });
  const act_2272469684735879_script_3op2xo2de = new actionTypes.ScriptAction({
    actionArgs: act_2272469684735879_script_3op2xo2de_getActionArgs,
  });
  const inst_evh_2272469684735879_saved_1upuvw1e2 =
    new actionTypes.EventHandlerImpl({
      action: act_2272469684735879_script_3op2xo2de,
      event: 'saved',
      displayName: 'script',
    });
  const act_2272469684735879_reloadComponentData_biv1i3t5j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2272469684735879_saved_554l1nu5t =
    new actionTypes.EventHandlerImpl({
      action: act_2272469684735879_reloadComponentData_biv1i3t5j,
      event: 'saved',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2272469684735879_saved = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2272469684735879_saved_1upuvw1e2,
      inst_evh_2272469684735879_saved_554l1nu5t,
    ],
    event: 'saved',
  });
  const act_2272469684735879_script_dxoazoqo3 = new actionTypes.ScriptAction({
    actionArgs: act_2272469684735879_script_dxoazoqo3_getActionArgs,
  });
  const inst_evh_2272469684735879_cancelEdit_c6cfdb521 =
    new actionTypes.EventHandlerImpl({
      action: act_2272469684735879_script_dxoazoqo3,
      event: 'cancelEdit',
      displayName: 'script',
    });
  const inst_evh_2272469684735879_cancelEdit =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2272469684735879_cancelEdit_c6cfdb521],
      event: 'cancelEdit',
    });
  const act_3373384577795494_script_fjko0iwsx = new actionTypes.ScriptAction({
    actionArgs: act_3373384577795494_script_fjko0iwsx_getActionArgs,
    displayName: 'createRule',
  });
  const inst_evh_3373384577795494_clickPreventStop_bzeg9mf8q =
    new actionTypes.EventHandlerImpl({
      action: act_3373384577795494_script_fjko0iwsx,
      event: 'click.prevent.stop',
      displayName: 'createRule',
    });
  const inst_evh_3373384577795494_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3373384577795494_clickPreventStop_bzeg9mf8q],
      event: 'click.prevent.stop',
    });
  const act_3601807637515735_script_6s3ah2psz = new actionTypes.ScriptAction({
    actionArgs: act_3601807637515735_script_6s3ah2psz_getActionArgs,
    displayName: 'createRule',
  });
  const inst_evh_3601807637515735_clickPreventStop_8e39z3q2t =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_script_6s3ah2psz,
      event: 'click.prevent.stop',
      displayName: 'createRule',
    });
  const inst_evh_3601807637515735_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3601807637515735_clickPreventStop_8e39z3q2t],
      event: 'click.prevent.stop',
    });
  const act_3322283983417540_closeComponent_v1hq7m4j0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_3322283983417540_clickPreventStop_ce087e5cd =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_closeComponent_v1hq7m4j0,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_3322283983417540_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3322283983417540_clickPreventStop_ce087e5cd],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_m88zsh2nq,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_0ilw5cvid,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_w6a0vc59f,
    inst_evh_8564662037462133_close,
    inst_evh_4615685607825208_clickPreventStop_9483ddt01,
    reload: inst_evh_4615685607825208_clickPreventStop_9483ddt01,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_close_gboev69i9,
    inst_evh_2095089031469157_clickPreventStop_3g9k5mkfv,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_5105091150304644_saved_55y6ze8mu,
    inst_evh_5105091150304644_saved_nc74kpuwj,
    inst_evh_5105091150304644_saved,
    inst_evh_5105091150304644_cancelEdit_m2ey1an1n,
    inst_evh_5105091150304644_cancelEdit,
    inst_evh_8387648034966287_clickPreventStop_pxwl28y4e,
    editItem: inst_evh_8387648034966287_clickPreventStop_pxwl28y4e,
    inst_evh_8387648034966287_clickPreventStop,
    inst_evh_6964713724626057_deleted_0f1635r4u,
    inst_evh_6964713724626057_clickPreventStop_pcqiqtt0e,
    deleteItem: inst_evh_6964713724626057_clickPreventStop_pcqiqtt0e,
    inst_evh_6964713724626057_clickPreventStop,
    inst_evh_2272469684735879_saved_1upuvw1e2,
    inst_evh_2272469684735879_saved_554l1nu5t,
    inst_evh_2272469684735879_saved,
    inst_evh_2272469684735879_cancelEdit_c6cfdb521,
    inst_evh_2272469684735879_cancelEdit,
    inst_evh_3373384577795494_clickPreventStop_bzeg9mf8q,
    createRule: inst_evh_3373384577795494_clickPreventStop_bzeg9mf8q,
    inst_evh_3373384577795494_clickPreventStop,
    inst_evh_3601807637515735_clickPreventStop_8e39z3q2t,
    inst_evh_3601807637515735_clickPreventStop,
    inst_evh_3322283983417540_clickPreventStop_ce087e5cd,
    inst_evh_3322283983417540_clickPreventStop,
  };

  const evh_7315092382398562_close_m88zsh2nq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_m88zsh2nq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_0ilw5cvid = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0ilw5cvid.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_w6a0vc59f = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_w6a0vc59f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_9483ddt01 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_9483ddt01.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_gboev69i9 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_gboev69i9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_3g9k5mkfv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_3g9k5mkfv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_saved_55y6ze8mu = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_saved_55y6ze8mu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_saved_nc74kpuwj = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_saved_nc74kpuwj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_saved = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_cancelEdit_m2ey1an1n = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_cancelEdit_m2ey1an1n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_cancelEdit = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_cancelEdit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8387648034966287_clickPreventStop_pxwl28y4e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8387648034966287_clickPreventStop_pxwl28y4e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8387648034966287_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8387648034966287_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6964713724626057_deleted_0f1635r4u = async (event, scope) => {
    return await actions.inst_evh_6964713724626057_deleted_0f1635r4u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6964713724626057_clickPreventStop_pcqiqtt0e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6964713724626057_clickPreventStop_pcqiqtt0e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6964713724626057_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6964713724626057_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_saved_1upuvw1e2 = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_saved_1upuvw1e2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_saved_554l1nu5t = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_saved_554l1nu5t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_saved = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_cancelEdit_c6cfdb521 = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_cancelEdit_c6cfdb521.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_cancelEdit = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_cancelEdit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3373384577795494_clickPreventStop_bzeg9mf8q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3373384577795494_clickPreventStop_bzeg9mf8q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3373384577795494_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3373384577795494_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop_8e39z3q2t = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop_8e39z3q2t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop_ce087e5cd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop_ce087e5cd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BillingRules"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <ItpBox :gap="2">
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :tooltip="gettext('Reload')"
            :spinning="actions.reload.isRunning"
            :text="gettext('Reload')"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            icon="lets-icons:import"
            :text="pgettext('Templates', 'Import')"
            variant="light"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            icon="lets-icons:export"
            :text="pgettext('BillingRules', 'Export')"
            variant="light"
            :disabled="!items || !items.length"
            use-auth
            force-download
            downloadFileName="billing-rules.json"
            :href="
              $fn.urlFor('export_object_by_type', {
                object_type: 'billing-rule',
              })
            "
          >
          </ItpButton>
        </ItpBox>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <BListGroup
        v-if="(items && items.length) || createItem"
        :style="b_1040167445267876_style"
      >
        <template v-for="item in items">
          <BListGroupItem :key="item.id" v-if="items && items.length">
            <ItpBox alignItems="start">
              <BillingRule
                :item="item"
                :editing="editingItemID == item.id"
                class="flex-grow-1"
                @saved="evh_5105091150304644_saved($event, { item })"
                @cancelEdit="evh_5105091150304644_cancelEdit($event, { item })"
              >
              </BillingRule>
              <div class="ms-3" :style="b_8527427120829392_style">
                <BDropdown
                  size="sm"
                  toggle-class="btn-default-borderless"
                  no-caret
                  variant="none"
                >
                  <template #button-content>
                    <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
                  </template>
                  <BDropdownItemButton
                    @click.prevent.stop="
                      evh_8387648034966287_clickPreventStop($event, { item })
                    "
                  >
                    <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
                  </BDropdownItemButton>
                  <BDropdownItemButton
                    @click.prevent.stop="
                      evh_6964713724626057_clickPreventStop($event, { item })
                    "
                  >
                    <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
                  </BDropdownItemButton>
                </BDropdown>
              </div>
            </ItpBox>
          </BListGroupItem>
        </template>
        <BListGroupItem v-if="createItem">
          <ItpBox alignItems="start">
            <BillingRule
              editing
              class="flex-grow-1"
              @saved="evh_2272469684735879_saved($event, {})"
              @cancelEdit="evh_2272469684735879_cancelEdit($event, {})"
            >
            </BillingRule>
            <div class="ms-3" :style="b_8363486085232621_style"></div>
          </ItpBox>
        </BListGroupItem>
        <BListGroupItem class="border-0" v-if="!createItem">
          <ItpBox justifyContent="center">
            <ItpButton
              variant="primary"
              :text="pgettext('BillingRules', 'Create rule')"
              :disabled="editingItemID != null"
              class="w-auto"
              @click.prevent.stop="
                evh_3373384577795494_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
      <ItpBox
        direction="column"
        alignItems="center"
        justifyContent="center"
        v-if="(!items || !items.length) && !createItem"
        :style="b_4186377094414664_style"
      >
        <p>
          {{ pgettext('BillingRules', 'No rules have been defined yet.') }}
        </p>
        <ItpButton
          variant="primary"
          :text="pgettext('BillingRules', 'Create rule')"
          @click.prevent.stop="
            evh_3601807637515735_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="ms-auto">
          <ItpButton
            :text="gettext('Close')"
            variant="default"
            @click.prevent.stop="
              evh_3322283983417540_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
